import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin, Globe, MessageCircle, Linkedin, Instagram, Facebook, CalendarClock, ArrowLeft, Package, Wrench, Image as ImageIcon, Radar, BadgeCheck, Info, Building2, Mail, Lock, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ShareDialog } from '@/components/common/ShareDialog';
import { ReportDialog, ContactDialog } from '@/components/common/Dialogs';
import { CompanyAvatar, GeneratedCover, Chip, DemoBadge, EmptyState, CategoryIcon, SponsoredBadge } from '@/components/common/Primitives';
import { OpportunityCard, CompanyCard } from '@/components/cards/Cards';
import { MiniStateMap } from '@/components/map/MiniStateMap';
import { AdCover } from '@/components/common/AdCover';
import { PortfolioGrid } from '@/components/common/PortfolioGrid';
import { useData } from '@/hooks/useData';
import { VisitorOnly } from '@/hooks/useIsVisitor';
import { useSeo } from '@/lib/seo';
import { formatDate } from '@/lib/format';
import { stateName } from '@/data/states';
import { BUSINESS_TYPE_NAME } from '@/data/taxonomy';
import { contactService } from '@/services/contacts';

const Block = ({ title, icon: Icon, children, testId }) => (
  <section className="surface p-5" data-testid={testId}>
    <h2 className="mb-4 flex items-center gap-2 font-display text-base font-semibold"><Icon className="h-4 w-4 text-primary" /> {title}</h2>
    {children}
  </section>
);

const SOCIAL = [
  { k: 'linkedin', label: 'LinkedIn', icon: Linkedin, base: 'https://www.linkedin.com/company/' },
  { k: 'instagram', label: 'Instagram', icon: Instagram, base: 'https://www.instagram.com/' },
  { k: 'facebook', label: 'Facebook', icon: Facebook, base: 'https://www.facebook.com/' },
];

export default function CompanyDetail() {
  const { id } = useParams();
  const { companyById, categoryById, publicOpportunities, publicCompanies, user, myCompany } = useData();
  const c = companyById[id];
  useSeo({ title: c ? c.name : 'Empresa não encontrada', description: c ? `${c.tagline} — ${c.location.city}/${c.location.uf}` : undefined, type: 'profile' });
  const opps = useMemo(() => publicOpportunities.filter((o) => o.companyId === id), [publicOpportunities, id]);
  const similar = useMemo(() => (c ? publicCompanies.filter((x) => x.id !== c.id && x.categoryId === c.categoryId).slice(0, 3) : []), [publicCompanies, c]);

  const isOwner = !!myCompany && !!c && myCompany.id === c.id;
  const [contacts, setContacts] = useState(null);

  // Contatos privados: só chegam se você for o dono ou se o pedido foi aceito pela empresa
  useEffect(() => {
    if (!user || !id || isOwner) {
      setContacts(null);
      return undefined;
    }
    let active = true;
    contactService
      .contacts(id)
      .then((row) => { if (active) setContacts(row); })
      .catch(() => { if (active) setContacts(null); });
    return () => { active = false; };
  }, [user?.id, id, isOwner]);

  if (!c || c.status !== 'ativa') {
    return (
      <div className="container-x py-16">
        <EmptyState icon={Building2} title={c ? 'Perfil indisponível' : 'Empresa não encontrada'} text={c ? 'Este perfil está suspenso pela moderação.' : 'O perfil pode ter sido removido ou o link está incorreto.'} testId="company-not-found" />
        <div className="mt-4 text-center"><Button asChild variant="secondary"><Link to="/empresas"><ArrowLeft /> Voltar ao diretório</Link></Button></div>
      </div>
    );
  }
  const cat = categoryById[c.categoryId];
  const socials = SOCIAL.filter((s) => c.socials?.[s.k]);
  const validated = c.cnpjStatus === 'valido';
  const hasAccess = isOwner || !!contacts;
  const whatsapp = isOwner ? c.whatsapp : contacts?.whatsapp;
  const email = isOwner ? c.email : contacts?.email;
  const cnpj = isOwner ? c.cnpj : contacts?.cnpj;

  return (
    <div className="pb-6" data-testid="company-detail">
      <AdCover seed={c.name}>
        <div className="container-x relative flex h-full items-start justify-between pt-5">
          <Button asChild variant="ghost" size="sm" className="bg-black/40 hover:bg-black/60"><Link to="/empresas"><ArrowLeft /> Diretório</Link></Button>
          <VisitorOnly><span className="rounded bg-black/60 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Capa ilustrativa</span></VisitorOnly>
        </div>
      </AdCover>
      <div className="container-x">
        <div className="relative z-10 -mt-10 flex flex-col gap-5 sm:-mt-12 md:flex-row md:items-end md:justify-between">
          <div className="flex items-start gap-4">
            <CompanyAvatar company={c} size={96} className="rounded-2xl border-white/20 shadow-[0_18px_60px_rgba(0,0,0,0.6)]" />
            <div className="pt-12 sm:pt-14">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl" data-testid="company-name">{c.name}</h1>
                {validated && <span className="inline-flex items-center gap-1 rounded-md border border-cyan-300/30 bg-cyan-300/10 px-2 py-0.5 text-xs text-cyan-100" data-testid="company-verified-badge"><BadgeCheck className="h-3.5 w-3.5" /> Verificado</span>}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{c.tagline}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!isOwner && <ContactDialog company={c} testId="company-contact-button" />}
            <ShareDialog path={`/empresas/${c.id}`} title={`${c.name} — Brasil Livre Business`} testId="company-share-button" />
            <ReportDialog targetType="empresa" targetId={c.id} testId="company-report-button" />
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {c.isDemo && <VisitorOnly><DemoBadge label="Empresa fictícia" /></VisitorOnly>}
          {c.sponsored && <SponsoredBadge />}
          <Chip><CategoryIcon name={cat?.icon} className="mr-1 h-3 w-3 text-primary" />{cat?.name}</Chip>
          {(c.extraCategoryIds || []).map((x) => <Chip key={x}>{categoryById[x]?.name}</Chip>)}
          <Chip>{BUSINESS_TYPE_NAME[c.businessType] || 'Negócio'}</Chip>
          <Chip><MapPin className="mr-1 h-3 w-3" />{c.location.city} · {stateName(c.location.uf)}</Chip>
        </div>

        <div className="mt-8 grid gap-5 lg:grid-cols-3">
          <div className="space-y-5 lg:col-span-2">
            <Block title="Sobre a empresa" icon={Info} testId="company-about">
              <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{c.description}</p>
            </Block>
            <div className="grid gap-5 md:grid-cols-2">
              <Block title="Serviços" icon={Wrench} testId="company-services">
                {c.services?.length ? <ul className="space-y-2 text-sm">{c.services.map((s) => <li key={s} className="flex gap-2"><span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />{s}</li>)}</ul> : <p className="text-sm text-muted-foreground">Nenhum serviço informado.</p>}
              </Block>
              <Block title="Produtos" icon={Package} testId="company-products">
                {c.products?.length ? <ul className="space-y-2 text-sm">{c.products.map((s) => <li key={s} className="flex gap-2"><span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-accent" />{s}</li>)}</ul> : <p className="text-sm text-muted-foreground">Nenhum produto informado.</p>}
              </Block>
            </div>
            <Block title="Portfólio" icon={ImageIcon} testId="company-portfolio">
              {c.portfolio?.length ? <PortfolioGrid items={c.portfolio} seed={c.name} /> : <p className="text-sm text-muted-foreground">Nenhum item de portfólio publicado.</p>}
            </Block>
            <Block title={`Oportunidades publicadas (${opps.length})`} icon={Radar} testId="company-opportunities">
              {opps.length ? <div className="grid gap-3">{opps.map((o) => <OpportunityCard key={o.id} opp={o} dense />)}</div> : <p className="text-sm text-muted-foreground">Esta empresa ainda não publicou oportunidades.</p>}
            </Block>
          </div>

          <aside className="space-y-5">
            <Block title="Localização" icon={MapPin} testId="company-location">
              <MiniStateMap uf={c.location.uf} city={c.location.city} />
              <p className="mt-3 text-sm">{c.location.city} · {stateName(c.location.uf)}</p>
            </Block>
            <Block title="Canais oficiais" icon={Globe} testId="company-channels">
              {contacts && !isOwner && (
                <div className="mb-3 flex items-center gap-2 rounded-md border border-emerald-400/25 bg-emerald-400/5 px-3 py-2 text-xs text-emerald-200" data-testid="company-access-granted">
                  <CheckCircle2 className="h-4 w-4" /> Esta empresa liberou os contatos para você.
                </div>
              )}
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  {c.website ? (c.isDemo ? <span className="font-mono text-xs text-muted-foreground">{c.website.replace('https://', '')} <span className="opacity-70">(exemplo)</span></span> : <a href={c.website} target="_blank" rel="noopener noreferrer nofollow" className="text-accent hover:underline" data-testid="company-website-link">{c.website.replace(/^https?:\/\//, '')}</a>) : <span className="text-muted-foreground">Site não informado</span>}
                </li>
                <li className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-muted-foreground" />
                  {hasAccess && whatsapp ? (
                    <a href={`https://wa.me/55${whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-emerald-300 hover:underline" data-testid="company-whatsapp-link">WhatsApp comercial {whatsapp}</a>
                  ) : hasAccess ? (
                    <span className="text-muted-foreground">WhatsApp não informado pela empresa</span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-muted-foreground" data-testid="company-whatsapp-private"><Lock className="h-3.5 w-3.5" /> WhatsApp privado</span>
                  )}
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  {hasAccess && email ? (
                    <a href={`mailto:${email}`} className="text-accent hover:underline" data-testid="company-email-link">{email}</a>
                  ) : hasAccess ? (
                    <span className="text-muted-foreground">E-mail não informado pela empresa</span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-muted-foreground" data-testid="company-email-private"><Lock className="h-3.5 w-3.5" /> E-mail privado</span>
                  )}
                </li>
                {socials.length ? socials.map(({ k, label, icon: Icon, base }) => (
                  <li key={k} className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    {c.isDemo ? <span className="text-muted-foreground">{label}: @{c.socials[k]} <span className="opacity-70">(exemplo)</span></span> : <a href={c.socials[k].startsWith('http') ? c.socials[k] : base + c.socials[k]} target="_blank" rel="noopener noreferrer nofollow" className="text-accent hover:underline">{label}</a>}
                  </li>
                )) : <li className="text-muted-foreground">Redes sociais não informadas</li>}
              </ul>
              {((hasAccess && cnpj) || validated) && (
                <p className={`mt-4 rounded-md border px-3 py-2 font-mono text-[11px] ${validated ? 'border-emerald-400/25 bg-emerald-400/5 text-emerald-200' : 'border-white/10 bg-white/[0.03] text-muted-foreground'}`} data-testid="company-cnpj">
                  {hasAccess && cnpj
                    ? `CNPJ ${cnpj} · ${validated ? 'validado na Receita Federal' : 'aguardando validação'}`
                    : 'CNPJ validado na Receita Federal · número disponível após aprovação do pedido'}
                </p>
              )}
            </Block>
            <div className="surface flex items-center gap-2 p-4 text-xs text-muted-foreground" data-testid="company-updated-at">
              <CalendarClock className="h-4 w-4" /> Perfil atualizado em <span className="font-mono text-foreground">{formatDate(c.updatedAt)}</span>
            </div>
          </aside>
        </div>

        {similar.length > 0 && (
          <section className="mt-12">
            <h2 className="mb-4 text-xl font-semibold">Empresas da mesma categoria</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{similar.map((s) => <CompanyCard key={s.id} company={s} />)}</div>
          </section>
        )}
      </div>
    </div>
  );
}